import { useEffect, type ReactNode } from 'react';
import { MAX_SPOT_SECONDS } from '@rabbitfuel/shared';
import { BrandMark, Footer, LegalLinks, Topbar } from '../components/Chrome.tsx';
import { OPERATOR } from '../legal/operator.ts';

/*
 * Impressum und Datenschutzerklaerung.
 *
 * Die Texte haengen an keinem Router, keinem Konto und keiner API -- sie
 * muessen auch vor der Zugangssperre lesbar sein (`LegalStandalone`). Ein
 * Impressum hat „staendig verfuegbar" zu sein, auch fuer jemanden ohne
 * Zugangspasswort.
 */

export type LegalKind = 'impressum' | 'datenschutz';

export const LEGAL_PATHS: Record<LegalKind, string> = {
  impressum: '/impressum',
  datenschutz: '/datenschutz',
};

export const legalKindOf = (path: string): LegalKind | null =>
  path === LEGAL_PATHS.impressum ? 'impressum' : path === LEGAL_PATHS.datenschutz ? 'datenschutz' : null;

const TITLES: Record<LegalKind, string> = { impressum: 'Impressum', datenschutz: 'Datenschutzerklärung' };

/** Innerhalb der App: mit Kopf- und Fusszeile. */
export function LegalPage({ kind }: { kind: LegalKind }) {
  useLegalTitle(kind);
  return (
    <div className="app">
      <Topbar />
      <main className="shell legal">{kind === 'impressum' ? <Imprint /> : <Privacy />}</main>
      <Footer />
    </div>
  );
}

/**
 * Vor der Zugangssperre: ohne Router und ohne Konto -- davon soll vor der
 * Freigabe nichts laden. Die Links sind deshalb gewoehnliche Anker.
 */
export function LegalStandalone({ kind }: { kind: LegalKind }) {
  useLegalTitle(kind);
  return (
    <div className="app">
      <header className="topbar">
        <div className="shell topbar__inner">
          <a href="/" className="brand">
            <BrandMark />
            <span>
              Last Stream Standing
              <small>zurück</small>
            </span>
          </a>
        </div>
      </header>
      <main className="shell legal">{kind === 'impressum' ? <Imprint /> : <Privacy />}</main>
      <footer className="footer">
        <div className="shell footer__inner">
          <span>Last Stream Standing</span>
          <LegalLinks plain />
        </div>
      </footer>
    </div>
  );
}

function useLegalTitle(kind: LegalKind) {
  useEffect(() => {
    const before = document.title;
    document.title = `${TITLES[kind]} — Last Stream Standing`;
    window.scrollTo(0, 0);
    return () => {
      document.title = before;
    };
  }, [kind]);
}

/** Platzhalter in [Klammern] gelb hervorheben -- sie sollen auffallen, bis sie ersetzt sind. */
function V({ children }: { children: string }) {
  return /^\[.*\]$/s.test(children) ? <mark className="legal__ph">{children}</mark> : <>{children}</>;
}

/** Eine Zeile nur, wenn die Angabe gilt (`null` = gilt nicht). */
function Optional({ label, value }: { label: string; value: string | null }) {
  if (value === null) return null;
  return (
    <p>
      {label}: <V>{value}</V>
    </p>
  );
}

function Address() {
  return (
    <p>
      <V>{OPERATOR.name}</V>
      <br />
      <V>{OPERATOR.street}</V>
      <br />
      <V>{OPERATOR.city}</V>
      <br />
      <V>{OPERATOR.country}</V>
    </p>
  );
}

function Mail() {
  const placeholder = /^\[.*\]$/.test(OPERATOR.email);
  return placeholder ? <V>{OPERATOR.email}</V> : <a href={`mailto:${OPERATOR.email}`}>{OPERATOR.email}</a>;
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <h2>{title}</h2>
      {children}
    </section>
  );
}

/* ------------------------------------------------------------ Impressum */

export function Imprint() {
  return (
    <article className="legal__doc">
      <h1 className="page-title">Impressum</h1>

      <Section title="Angaben gemäß § 5 Digitale-Dienste-Gesetz (DDG)">
        <Address />
        <Optional label="Vertreten durch" value={OPERATOR.representedBy} />
      </Section>

      <Section title="Kontakt">
        <p>
          Telefon: <V>{OPERATOR.phone}</V>
          <br />
          E-Mail: <Mail />
        </p>
      </Section>

      {(OPERATOR.register || OPERATOR.vatId || OPERATOR.economicId) && (
        <Section title="Register und Steuern">
          <Optional label="Registereintrag" value={OPERATOR.register} />
          <Optional label="Umsatzsteuer-Identifikationsnummer gemäß § 27a UStG" value={OPERATOR.vatId} />
          <Optional label="Wirtschafts-Identifikationsnummer gemäß § 139c AO" value={OPERATOR.economicId} />
        </Section>
      )}

      <Section title="Verantwortlich für den Inhalt nach § 18 Abs. 2 MStV">
        <p>
          <V>{OPERATOR.contentResponsible}</V>
        </p>
      </Section>

      <Section title="Inhalte der Veranstalter">
        <p>
          Last Stream Standing ist eine Plattform, auf der Vereine und Veranstalter ihre Events selbst live übertragen.
          Livestreams, Ticker, Startlisten, Ergebnisse, Event-Beschreibungen, Sponsorenwerbung und Chat-Beiträge stellen
          die jeweiligen Veranstalter und Teilnehmenden ein. Wir prüfen diese Inhalte nicht vorab und machen sie uns nicht
          zu eigen. Verantwortlich ist der Veranstalter, der auf der jeweiligen Event-Seite genannt ist.
        </p>
      </Section>

      <Section title="Rechtswidrige Inhalte melden">
        <p>
          Zentrale Kontaktstelle für Behörden, die EU-Kommission und Nutzer:innen nach Art. 11 und 12 des Digital
          Services Act (Verordnung (EU) 2022/2065): <Mail />. Du erreichst uns auf Deutsch und Englisch.
        </p>
        <p>
          Hältst du einen Stream, einen Chat-Beitrag oder einen anderen Inhalt für rechtswidrig, schreib uns an diese
          Adresse. Damit wir handeln können, nenne bitte:
        </p>
        <ul>
          <li>den Link zum Event und den genauen Inhalt — bei einem Stream mit Uhrzeit,</li>
          <li>warum der Inhalt aus deiner Sicht rechtswidrig ist,</li>
          <li>
            deinen Namen und deine E-Mail-Adresse (nicht nötig bei Meldungen zu Darstellungen sexuellen Missbrauchs von
            Kindern),
          </li>
          <li>dass deine Angaben nach bestem Wissen richtig und vollständig sind.</li>
        </ul>
        <p>Wir bestätigen den Eingang, prüfen die Meldung zügig und sagen dir, wie wir entschieden haben.</p>
      </Section>

      <Section title="Verbraucherstreitbeilegung">
        <p>
          Wir sind nicht bereit und nicht verpflichtet, an Streitbeilegungsverfahren vor einer
          Verbraucherschlichtungsstelle teilzunehmen.
        </p>
      </Section>
    </article>
  );
}

/* ------------------------------------------------------- Datenschutz */

export function Privacy() {
  return (
    <article className="legal__doc">
      <h1 className="page-title">Datenschutzerklärung</h1>
      <p className="legal__lede">
        Hier steht, welche personenbezogenen Daten Last Stream Standing verarbeitet, wofür, wie lange und welche Rechte du hast.
        Stand: {OPERATOR.updated}.
      </p>

      <Section title="1. Verantwortlich">
        <Address />
        <p>
          E-Mail: <Mail />
          <br />
          Telefon: <V>{OPERATOR.phone}</V>
        </p>
        {OPERATOR.privacyOfficer && (
          <p>
            Datenschutzbeauftragte:r: <V>{OPERATOR.privacyOfficer}</V>
          </p>
        )}
      </Section>

      <Section title="2. Das Wichtigste in Kürze">
        <ul>
          <li>Keine Analyse-Tools, keine Werbe-Tracker, keine Cookies von Drittanbietern.</li>
          <li>Kamera, Mikrofon und Standort nutzt Last Stream Standing nur, wenn du sie selbst freigibst und senden willst.</li>
          <li>
            Livestreams laufen direkt zwischen den beteiligten Geräten. Unsere Server zeichnen keine Streams auf.
          </li>
          <li>Zuschauen und als Athlet:in senden geht ohne Konto; ein Konto brauchen nur Veranstalter.</li>
          <li>Sponsorenwerbung wird angezeigt, aber es fließen dafür keine Daten an Sponsoren.</li>
        </ul>
      </Section>

      <Section title="3. Aufruf der Website und Server-Protokolle">
        <p>
          Beim Aufruf verarbeitet unser Server technisch nötige Verbindungsdaten: IP-Adresse, Zeitpunkt, aufgerufene
          Adresse, Antwortstatus und Antwortzeit. Sie stehen in Server-Protokollen, die wir zur Fehlersuche und zur
          Abwehr von Angriffen nutzen und nach <V>{OPERATOR.logRetention}</V> löschen. Rechtsgrundlage ist unser
          berechtigtes Interesse an einem sicheren, funktionierenden Dienst (Art. 6 Abs. 1 lit. f DSGVO).
        </p>
        <p>
          Gehostet wird Last Stream Standing bei <V>{OPERATOR.hoster}</V>. Der Anbieter verarbeitet die Daten in unserem Auftrag
          (Art. 28 DSGVO). Die Verbindung ist per TLS verschlüsselt.
        </p>
      </Section>

      <Section title="4. Cookies und Speicher im Browser">
        <p>
          Last Stream Standing legt nur ab, was für die Funktion nötig ist, die du gerade nutzt. Dafür braucht es keine
          Einwilligung (§ 25 Abs. 2 Nr. 2 TDDDG). Nichts davon wird zum Wiedererkennen über andere Websites genutzt.
        </p>
        <div className="legal__table">
          <table>
            <thead>
              <tr>
                <th>Was</th>
                <th>Wofür</th>
                <th>Wie lange</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  Cookie <code>rf_gate</code>
                </td>
                <td>Merkt sich das Zugangspasswort, solange die Seite noch nicht öffentlich ist.</td>
                <td>bis der Browser geschlossen wird</td>
              </tr>
              <tr>
                <td>
                  <code>rabbitfuel:token</code>
                </td>
                <td>Hält Veranstalter angemeldet.</td>
                <td>bis zum Abmelden, höchstens 30 Tage</td>
              </tr>
              <tr>
                <td>
                  <code>rabbitfuel:session:…</code>, <code>rabbitfuel:device</code>, <code>rabbitfuel:capture:…</code>,{' '}
                  <code>rabbitfuel:segment</code>
                </td>
                <td>
                  Rolle, Anzeigename und ggf. Startnummer für ein Event; eine zufällige Kennung des Tabs, damit ein
                  laufender Stream ein Neuladen übersteht; die gewählte Kamera und der Hintergrund-Modus der Regie-Kamera.
                </td>
                <td>bis der Tab geschlossen wird</td>
              </tr>
              <tr>
                <td>
                  <code>rabbitfuel:theme</code>, <code>rabbitfuel:brand</code>, <code>rabbitfuel:watch:hidden</code>,{' '}
                  <code>rabbitfuel:regie-…</code>
                </td>
                <td>Hell/Dunkel, Farbe, für dich ausgeblendete Einblendungen, eingeklappte Bereiche.</td>
                <td>bis du den Website-Speicher löschst</td>
              </tr>
              <tr>
                <td>
                  <code>rabbitfuel:client</code>
                </td>
                <td>
                  Eine zufällige Kennung dieses Browsers, damit die Regie deine Botschaften dir zuordnen kann — für die
                  Zahl, die du schicken darfst, und eine mögliche Sperre.
                </td>
                <td>bis du den Website-Speicher löschst</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Section>

      <Section title="5. Konto für Veranstalter">
        <p>
          Für ein Konto verarbeiten wir E-Mail-Adresse, Anzeigename und dein Passwort — Letzteres nur als nicht
          umkehrbaren Hash. Wer ein Event anlegt, hinterlegt außerdem die E-Mail-Adressen der Personen, die Regie führen
          dürfen. Zweck ist der Zugang zur Regie; Rechtsgrundlage ist der Nutzungsvertrag (Art. 6 Abs. 1 lit. b DSGVO),
          für die Adressen weiterer Regie-Personen das berechtigte Interesse des Veranstalters an der Organisation seines
          Events (lit. f). Wir löschen das Konto, wenn du es verlangst — eine E-Mail an <Mail /> genügt.
        </p>
      </Section>

      <Section title="6. Events ansehen, mitmachen und chatten">
        <p>
          Wer einem Event beitritt, nennt einen Anzeigenamen — ein Spitzname reicht — und als Athlet:in die
          Startnummer. Der Name steht an deinen Chat-Beiträgen und bei der Regie. Sendest du, sehen alle im Event
          außerdem Name, Rolle und ggf. Startnummer an deiner Kamera — auch im Stream, wenn die Regie sie zeigt. Die Zahl
          der Zuschauenden sehen alle. Chat-Beiträge sehen alle im Event; wir speichern je Event die letzten 100, ältere
          fallen automatisch heraus. Rechtsgrundlage ist die Nutzung des Dienstes (Art. 6 Abs. 1 lit. b DSGVO).
        </p>
      </Section>

      <Section title="7. Kamera, Mikrofon und Livestream">
        <p>
          Senden ist freiwillig. Dein Browser fragt vorher nach Kamera und Mikrofon, und der Veranstalter muss dein Bild
          erst freigeben. Danach geht dein Bild mit Ton an die Regie und — wenn die Regie es ins Hauptbild oder ins kleine
          Fenster schaltet — an alle, die das Event ansehen. Rechtsgrundlage ist deine Einwilligung (Art. 6 Abs. 1 lit. a
          DSGVO). Du kannst sie jederzeit mit „Senden beenden“ widerrufen; was bis dahin gesendet wurde, bleibt davon
          unberührt.
        </p>
        <p>
          Bild und Ton laufen per WebRTC direkt zwischen den Geräten, nicht über unseren Server, und werden von uns nicht
          aufgezeichnet. Bei solchen direkten Verbindungen sehen die verbundenen Geräte technisch bedingt gegenseitig ihre
          IP-Adressen.
        </p>
        <p>
          Welche Bilder in den Stream kommen, entscheidet der Veranstalter. Bitte achte darauf, wen du filmst, und
          filme niemanden gegen seinen Willen. Bist du unter 16, frag bitte vorher deine Eltern.
        </p>
        <p>
          <b>Botschaften an die Regie.</b> Du kannst eine kurze Video- oder Sprachnachricht (bis 90 Sekunden)
          aufnehmen oder aus deiner Galerie wählen und an die Regie schicken. Sie wird auf unserem Server gespeichert,
          zusammen mit deinem Anzeigenamen, deiner Rolle und ggf. Startnummer. Sehen kann sie zunächst nur die Regie des
          Events; sie entscheidet, ob sie im Livestream gezeigt wird — mit deinem Namen. Rechtsgrundlage ist deine
          Einwilligung, die du vor dem Hochladen gibst (Art. 6 Abs. 1 lit. a DSGVO). Löscht die Regie eine Botschaft,
          ist sie nach einer Stunde endgültig weg; alle übrigen löschen wir sieben Tage nach dem Ende des Events.
          Im Stream Gezeigtes kann beim Weitersenden (Abschnitt 11) dort aufgezeichnet werden.
        </p>
      </Section>

      <Section title="8. Verbindungsaufbau über STUN-Server">
        <p>
          Damit sich zwei Geräte direkt verbinden können, fragt dein Browser einen STUN-Server nach der eigenen
          öffentlichen Adresse. Dabei gehen deine IP-Adresse und ein Netzwerk-Port an den Betreiber dieses Servers. Wir
          nutzen dafür derzeit die STUN-Server von Google (Google LLC, USA). Google ist nach dem EU-US Data Privacy
          Framework zertifiziert; für die USA gilt damit der Angemessenheitsbeschluss der EU-Kommission (Art. 45 DSGVO).
          Rechtsgrundlage ist unser berechtigtes Interesse an stabilen Verbindungen (Art. 6 Abs. 1 lit. f DSGVO).
        </p>
      </Section>

      <Section title="9. Standort">
        <p>
          Manche Veranstalter erlauben das Senden nur in einem Bereich rund um das Event, und manche nehmen
          Botschaften von Zuschauer:innen nur von vor Ort an. Dann fragt dein Browser beim Senden bzw. vor der
          Aufnahme einer Botschaft nach deinem Standort. Der Server prüft damit, ob du im Bereich bist; gespeichert und an die Regie
          gegeben wird nur das Ergebnis — „vor Ort“ oder „außerhalb“ mit Entfernung und Genauigkeit, nicht deine
          Koordinaten. Rechtsgrundlage ist deine Einwilligung über die Abfrage des Browsers (Art. 6 Abs. 1 lit. a DSGVO).
        </p>
      </Section>

      <Section title="10. Startlisten, Ergebnisse und Ticker">
        <p>
          Veranstalter können Startlisten (Startnummer, Name, Team, Kategorie) und Zeiten eintragen oder aus ihrer
          Zeitnahme bei RACE RESULT abrufen lassen; der Abruf läuft über unseren Server mit dem Zugang des Veranstalters.
          Namen und Zeiten können im Ticker und in den Einblendungen des Streams erscheinen. Welche Daten das sind und
          wie Teilnehmende darüber informiert werden, entscheidet der Veranstalter — Fragen dazu richtest du am besten
          direkt an ihn. Wir löschen Chat, Startlisten und Ergebnisse eines Events <V>{OPERATOR.eventRetention}</V>.
        </p>
      </Section>

      <Section title="11. Weitersenden an YouTube und Twitch, Empfang per RTMP">
        <p>
          Ein Veranstalter kann das Hauptbild samt Ton und Einblendungen zusätzlich an seinen eigenen Kanal bei YouTube
          (Google Ireland Limited, Irland) oder Twitch (Twitch Interactive, Inc., USA) senden. Darüber entscheidet der
          Veranstalter. Das Video kann dort aufgezeichnet und dauerhaft veröffentlicht werden, und es gelten die
          Datenschutzbestimmungen des jeweiligen Anbieters.
        </p>
        <p>
          Twitch sitzt in den USA. Für Twitch gibt es keinen Angemessenheitsbeschluss der EU-Kommission; es ist daher
          nicht auszuschließen, dass US-Behörden auf dort gespeicherte Daten zugreifen. <V>{OPERATOR.twitchBasis}</V>
        </p>
        <p>
          Umgekehrt kann ein Veranstalter Videosignale per RTMP an unseren Server senden lassen — etwa von einer Drohne
          oder einer App, die den Bildschirm eines Handys überträgt. Dafür vergibt er einen geheimen Schlüssel. Unser
          Server verarbeitet dabei die IP-Adresse des sendenden Geräts und reicht Bild und Ton an Regie und Zuschauende
          weiter; aufgezeichnet wird nichts. Was im Bild zu sehen ist, verantwortet der Veranstalter wie bei jeder
          anderen Kamera. Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO.
        </p>
      </Section>

      <Section title="12. Sponsoren und Werbung">
        <p>
          Veranstalter können Logos ihrer Sponsoren ins Bild legen und Werbespots von bis zu {MAX_SPOT_SECONDS / 60}{' '}
          Minuten abspielen; ein Spot ist im Bild als „Werbung“ gekennzeichnet. Logos und Spots kommen von unserem
          Server. Es gibt keine personalisierte Werbung, und es gehen keine Daten über dich an Sponsoren.
        </p>
      </Section>

      <Section title="13. Karten, Ortssuche und Wetter">
        <p>
          Wenn ein Veranstalter in den Event-Einstellungen den Sendebereich festlegt, lädt die Karte Kacheln direkt von
          den Servern der OpenStreetMap Foundation (Cambridge, Vereinigtes Königreich). Dabei geht die IP-Adresse
          dorthin. Für das Vereinigte Königreich gilt ein Angemessenheitsbeschluss der EU-Kommission. Zuschauende und
          Athlet:innen betrifft das nicht. Rechtsgrundlage: Art. 6 Abs. 1 lit. f DSGVO.
        </p>
        <p>
          Für die Wetteranzeige fragt unser Server — nicht dein Browser — beim Meteorologischen Institut Norwegens (MET
          Norway, Oslo) die Vorhersage für die Koordinaten des Event-Orts ab und sucht diese gegebenenfalls über den
          Ortsdienst Nominatim der OpenStreetMap Foundation aus der Ortsangabe des Events heraus. Dabei gehen nur
          Koordinaten und Ortsname des Events dorthin, keine Daten über dich. Die Wetterdaten stehen unter der Lizenz
          CC BY 4.0 (© MET Norway).
        </p>
      </Section>

      <Section title="14. Hintergrund freistellen">
        <p>
          Das Weichzeichnen oder Ersetzen des Hintergrunds der Regie-Kamera rechnet ein KI-Modell direkt im Browser. Das
          Modell kommt von unserem Server; Kamerabilder verlassen dafür nicht das Gerät.
        </p>
      </Section>

      <Section title="15. Weitergabe und Speicherdauer">
        <p>
          Wir verkaufen keine Daten. Empfänger sind nur die oben genannten: der Veranstalter des jeweiligen Events,
          unser Hosting-Anbieter, die Betreiber der STUN-Server, YouTube oder Twitch (wenn der Veranstalter dorthin sendet)
          und die OpenStreetMap Foundation (nur Veranstalter). Wir speichern Daten nur so lange, wie es der jeweilige
          Zweck verlangt oder das Gesetz es vorschreibt. Eine automatisierte Entscheidung oder ein Profiling im Sinne von
          Art. 22 DSGVO findet nicht statt.
        </p>
        <p>
          Du bist weder gesetzlich noch vertraglich verpflichtet, uns Daten zu geben. Ohne Anzeigenamen kannst du nicht
          mitchatten, ohne Konto keine Regie führen, ohne Kamera- und gegebenenfalls Standortfreigabe nicht senden —
          zuschauen geht immer.
        </p>
      </Section>

      <Section title="16. Kontakt per E-Mail">
        <p>
          Schreibst du uns, verarbeiten wir deine Adresse und deine Nachricht, um sie zu beantworten (Art. 6 Abs. 1 lit. b
          bzw. f DSGVO), und löschen sie, wenn die Sache erledigt ist und keine Aufbewahrungspflicht besteht. Meldungen
          rechtswidriger Inhalte bewahren wir so lange auf, wie es zur Bearbeitung und zum Nachweis nötig ist.
        </p>
      </Section>

      <Section title="17. Deine Rechte">
        <p>Du hast das Recht auf</p>
        <ul>
          <li>Auskunft über deine Daten (Art. 15 DSGVO),</li>
          <li>Berichtigung (Art. 16) und Löschung (Art. 17),</li>
          <li>Einschränkung der Verarbeitung (Art. 18) und Datenübertragbarkeit (Art. 20),</li>
          <li>Widerruf einer Einwilligung mit Wirkung für die Zukunft (Art. 7 Abs. 3).</li>
        </ul>
        <p>
          Eine E-Mail an <Mail /> genügt. Außerdem kannst du dich bei einer Datenschutz-Aufsichtsbehörde beschweren (Art.
          77 DSGVO), etwa bei der für uns zuständigen: <V>{OPERATOR.authority}</V>.
        </p>
      </Section>

      {/* Art. 21 Abs. 4 DSGVO verlangt den Hinweis getrennt von den uebrigen Informationen. */}
      <Section title="18. Widerspruchsrecht">
        <p className="legal__notice">
          Verarbeiten wir Daten auf Grundlage berechtigter Interessen (Art. 6 Abs. 1 lit. f DSGVO), kannst du aus
          Gründen, die sich aus deiner besonderen Situation ergeben, jederzeit widersprechen (Art. 21 DSGVO). Wir
          verarbeiten die Daten dann nicht mehr, es sei denn, wir können zwingende schutzwürdige Gründe nachweisen, die
          deine Interessen überwiegen, oder die Verarbeitung dient der Geltendmachung, Ausübung oder Verteidigung von
          Rechtsansprüchen. Eine E-Mail an <Mail /> genügt.
        </p>
      </Section>
    </article>
  );
}
